import { useTranslation } from "react-i18next"
import { graphql, useFragment } from "react-relay"
import { Check } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button"
import { WithTooltip } from "@/hoc/with-tooltip"
import { appointmentDrawerCardHeaderReviewAction_appointment$key } from "@/modules/waiting-room/components/appointment-drawer/__generated__/appointmentDrawerCardHeaderReviewAction_appointment.graphql"
import { useAppointmentContext, useSelectNextAppointment } from "@/modules/waiting-room/contexts/appointment-context"
import { useUpdateAppointmentReviewStatusMutation } from "@/modules/waiting-room/services/useUpdateAppointmentReviewStatusMutation"

const AppointmentDrawerCardHeaderReviewActionFragment = graphql`
  fragment appointmentDrawerCardHeaderReviewAction_appointment on Appointment {
    id
    reviewStatus
    isCompleted
  }
`

type AppointmentDrawerCardHeaderProps = {
  appointmentRef: appointmentDrawerCardHeaderReviewAction_appointment$key
}

const AppointmentDrawerCardHeaderReviewAction = ({ appointmentRef }: AppointmentDrawerCardHeaderProps) => {
  const { t } = useTranslation()
  const { isOneOf: isUserOneOf } = useUser()

  const updateAppointmentReviewStatusMutation = useUpdateAppointmentReviewStatusMutation()
  const selectNextAppointment = useSelectNextAppointment()
  const isSharingWaitingRoomAppointment = useAppointmentContext((state) => state.waitingRoomPreviewDialog.isSharing)

  const { reviewStatus, isCompleted, id } = useFragment<appointmentDrawerCardHeaderReviewAction_appointment$key>(
    AppointmentDrawerCardHeaderReviewActionFragment,
    appointmentRef,
  )

  return (
    reviewStatus === "NEEDED" &&
    !isCompleted &&
    isUserOneOf(["MedicalSupervisor"]) && (
      <WithTooltip text={t("scheduling.waitingRoom.makeReviewedTooltip")}>
        <Button
          variant="circle"
          className="relative h-10 w-10 cursor-pointer"
          data-testid="review-appointment-btn"
          onClick={(event) => {
            event.stopPropagation()
            updateAppointmentReviewStatusMutation({ id, isReviewed: true })
            if (!isSharingWaitingRoomAppointment) selectNextAppointment()
          }}>
          <Check className="text-on-surface-subdued disabled:text-on-surface-disabled" />
        </Button>
      </WithTooltip>
    )
  )
}

export { AppointmentDrawerCardHeaderReviewAction }
